// Cmd+K command palette. Mirrors the Drillcut planner palette: a
// centred modal with a single search input, keyboard-navigable result
// list, and three categories — navigate (planner views), create
// (kind switcher entry points), search (live rocks / todos / issues).
// Esc / outside-click closes.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  CheckSquare,
  Flag,
  LayoutDashboard,
  Lightbulb,
  Plus,
  Search,
  Target,
  Users,
} from "lucide-react";
import { tpApi } from "../api";
import { DC } from "../sheets/createSlideOver/primitives";
import type { CreateKind } from "../sheets/createSlideOver/types";
import type { PlannerViewId } from "../../../../../../shared/team-planner/const";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: PlannerViewId) => void;
  onCreate: (kind: CreateKind) => void;
  onOpenItem: (kind: "rock" | "todo" | "issue", id: string) => void;
}

interface PaletteResult {
  id: string;
  group: "navigate" | "create" | "search";
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
}

export function CommandPalette({
  open,
  onClose,
  onNavigate,
  onCreate,
  onOpenItem,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [searchHits, setSearchHits] = useState<PaletteResult[]>([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Async live search across rocks (current quarter), todos (mine
  // scope), opportunities (open tab). Throttled by query change.
  useEffect(() => {
    if (!open) return;
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
      setSearchHits([]);
      return;
    }
    let alive = true;
    void (async () => {
      const [rocks, todos, issues] = await Promise.all([
        tpApi
          .listRocks(`${new Date().getFullYear()}-Q${Math.floor(new Date().getMonth() / 3) + 1}`, "all")
          .catch(() => []),
        tpApi.listTodos("all").catch(() => []),
        tpApi.listIssues("open").catch(() => []),
      ]);
      if (!alive) return;
      const hits: PaletteResult[] = [];
      for (const r of rocks) {
        if (r.title.toLowerCase().includes(q)) {
          hits.push({
            id: `rock-${r.id}`,
            group: "search",
            label: r.title,
            hint: `Rock · R${r.seq} · ${r.quarter}`,
            icon: <Target size={14} color="var(--bsp-blue)" />,
            run: () => onOpenItem("rock", r.id),
          });
        }
      }
      for (const t of todos) {
        if (t.title.toLowerCase().includes(q)) {
          hits.push({
            id: `todo-${t.id}`,
            group: "search",
            label: t.title,
            hint: `To-do · T${t.seq}`,
            icon: <CheckSquare size={14} color="var(--bsp-blue-2)" />,
            run: () => onOpenItem("todo", t.id),
          });
        }
      }
      for (const i of issues) {
        if (i.title.toLowerCase().includes(q)) {
          hits.push({
            id: `issue-${i.id}`,
            group: "search",
            label: i.title,
            hint: `Opportunity · O${i.seq}`,
            icon: <Lightbulb size={14} color="var(--bsp-orange)" />,
            run: () => onOpenItem("issue", i.id),
          });
        }
      }
      setSearchHits(hits.slice(0, 10));
    })();
    return () => {
      alive = false;
    };
  }, [query, open, onOpenItem]);

  const navigateActions: PaletteResult[] = useMemo(
    () => [
      { id: "nav-my-work", group: "navigate", label: "Go to My Work", icon: <LayoutDashboard size={14} />, run: () => onNavigate("my-work") },
      { id: "nav-rocks", group: "navigate", label: "Go to Rocks", icon: <Target size={14} />, run: () => onNavigate("rocks") },
      { id: "nav-milestones", group: "navigate", label: "Go to Milestones", icon: <Flag size={14} />, run: () => onNavigate("milestones") },
      { id: "nav-todos", group: "navigate", label: "Go to To-dos", icon: <CheckSquare size={14} />, run: () => onNavigate("todos") },
      { id: "nav-opportunities", group: "navigate", label: "Go to Opportunities", icon: <Lightbulb size={14} />, run: () => onNavigate("opportunities") },
      { id: "nav-health", group: "navigate", label: "Go to Health", icon: <Activity size={14} />, run: () => onNavigate("health") },
      { id: "nav-teams", group: "navigate", label: "Go to Teams", icon: <Users size={14} />, run: () => onNavigate("teams") },
    ],
    [onNavigate],
  );
  const createActions: PaletteResult[] = useMemo(
    () => [
      { id: "new-rock", group: "create", label: "New Rock", hint: "R", icon: <Plus size={14} />, run: () => onCreate("rock") },
      { id: "new-milestone", group: "create", label: "New Milestone", hint: "M", icon: <Flag size={14} />, run: () => onCreate("milestone") },
      { id: "new-todo", group: "create", label: "New To-do", hint: "T", icon: <Plus size={14} />, run: () => onCreate("todo") },
      { id: "new-opportunity", group: "create", label: "New Opportunity", hint: "O", icon: <Plus size={14} />, run: () => onCreate("opportunity") },
    ],
    [onCreate],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const fixed = [...navigateActions, ...createActions];
    const matched = q
      ? fixed.filter((r) => r.label.toLowerCase().includes(q))
      : fixed;
    return [...matched, ...searchHits];
  }, [query, navigateActions, createActions, searchHits]);

  // Reset highlight when results change.
  useEffect(() => {
    setHighlight(0);
  }, [filtered.length, query]);

  function run(idx: number) {
    const hit = filtered[idx];
    if (!hit) return;
    hit.run();
    onClose();
  }

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,24,28,0.45)",
        zIndex: 90,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(620px, 96%)",
          background: DC.surface,
          border: `1px solid ${DC.borderStrong}`,
          borderRadius: 12,
          boxShadow: "var(--bsp-shadow-pop)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "70vh",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${DC.border}` }}>
          <Search size={16} color={DC.grey} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or jump to…"
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              font: "400 15px/1 var(--bsp-font-sans)",
              background: "transparent",
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                onClose();
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, filtered.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                run(highlight);
              }
            }}
          />
          <kbd style={kbdStyle}>Esc</kbd>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 6 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: DC.grey, fontSize: 13 }}>
              No matches.
            </div>
          ) : (
            (["navigate", "create", "search"] as const).map((group) => {
              const rows = filtered
                .map((r, idx) => ({ r, idx }))
                .filter((x) => x.r.group === group);
              if (rows.length === 0) return null;
              return (
                <div key={group} style={{ marginBottom: 4 }}>
                  <div style={groupHeader}>{group}</div>
                  {rows.map(({ r, idx }) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => run(idx)}
                      onMouseEnter={() => setHighlight(idx)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "8px 10px",
                        border: 0,
                        background: idx === highlight ? DC.primaryPale : "transparent",
                        borderRadius: 4,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span style={{ width: 18, display: "inline-flex", color: DC.grey }}>{r.icon}</span>
                      <span style={{ flex: 1, fontSize: 13 }}>{r.label}</span>
                      {r.hint ? (
                        <span style={{ fontSize: 11, color: DC.grey }}>{r.hint}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const kbdStyle: React.CSSProperties = {
  fontFamily: "var(--bsp-font-mono)",
  fontSize: 10,
  padding: "2px 6px",
  background: "var(--bsp-surface-muted)",
  border: `1px solid ${DC.border}`,
  borderRadius: 4,
  color: DC.grey,
};
const groupHeader: React.CSSProperties = {
  font: "700 9px/1 var(--bsp-font-sans)",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: DC.grey,
  padding: "8px 10px 4px",
};
